import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRITICAL_ALLERGIES = new Set(['peanuts', 'tree_nuts', 'shellfish']);
const WARNING_ALLERGIES = new Set(['dairy', 'gluten', 'eggs', 'soy']);
const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Complete, PRICE-FREE party-host run sheet. Reads the shared
 * `/runsheet.json` payload (same source the PDF renders) so the printable
 * page and the downloadable PDF always match. Auto-prints when ?print=1.
 */
export const AdminRunSheet = () => {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const [rs, setRs] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    setRs(null);
    setErr(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/bookings/${bookingId}/runsheet.json`,
          { withCredentials: true },
        );
        if (mounted) setRs(res.data);
      } catch (e) {
        if (mounted) setErr(e.response?.data?.detail || 'Could not load run sheet');
      }
    })();
    return () => { mounted = false; };
  }, [bookingId]);

  useEffect(() => {
    if (rs && params.get('print') === '1') {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [rs, params]);

  if (err) return <div className="p-10 text-center text-red-600" data-testid="runsheet-print-error">{err}</div>;
  if (!rs) return <div className="p-10 text-center text-gray-500">Loading…</div>;

  const s = rs.summary || {};
  const bc = rs.birthday_child || {};
  const loc = rs.location || {};
  const gsum = rs.guest_summary || {};
  const printedAt = new Date().toLocaleString();

  const Section = ({ title, testid, children }) => (
    <section className="mb-5" data-testid={testid}>
      <h3 className="text-xs font-bold uppercase tracking-wider text-[#FF3B6B] mb-2 pb-1 border-b border-[#F2D9E1]">{title}</h3>
      {children}
    </section>
  );

  const Check = ({ children }) => (
    <li className="flex items-start gap-2 py-0.5">
      <span className="mt-0.5 w-3.5 h-3.5 border-2 border-[#1E1E24] rounded-sm flex-shrink-0" />
      <span>{children}</span>
    </li>
  );

  const allergyChips = (arr) => (arr || []).map((a) => {
    const key = String(a).toLowerCase().replace(/ /g, '_');
    const cls = CRITICAL_ALLERGIES.has(key)
      ? 'text-[#D1344D] font-bold'
      : WARNING_ALLERGIES.has(key) ? 'text-[#B3770A] font-bold' : 'text-[#1E1E24]';
    return <span key={a} className={cls}>{titleCase(a)}</span>;
  });

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white text-[#1E1E24] print:p-0 text-sm" data-testid="runsheet-print-view">
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          @page { margin: 1.1cm; }
        }
      `}</style>

      {/* Print toolbar */}
      <div className="no-print flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
        <h1 className="text-xl font-semibold">Party Host Run Sheet</h1>
        <div className="flex items-center gap-2">
          <a
            href={`${API_URL}/api/admin/bookings/${bookingId}/runsheet.pdf`}
            target="_blank" rel="noreferrer"
            className="rounded-full border border-[#FF3B6B] text-[#FF3B6B] px-4 py-2 text-sm font-semibold hover:bg-[#FF3B6B]/5"
            data-testid="runsheet-download-pdf"
          >
            Download PDF
          </a>
          <button
            type="button" onClick={() => window.print()}
            className="rounded-full bg-[#FF3B6B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#E62E58]"
            data-testid="runsheet-print-btn"
          >
            Print
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-[#FF3B6B] pb-4 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#FF3B6B] font-bold">Creative Birthdays · Party Host Run Sheet</p>
          <h2 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {s.child_name} {s.child_age != null && s.child_age !== '' && <span className="text-[#6B6B7A] font-normal">({s.child_age})</span>}
          </h2>
          <p className="text-[#6B6B7A] text-xs mt-1">{s.package_name} · {s.guest_count} guests · Ref {s.booking_reference}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#6B6B7A]">{s.date}</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Fredoka, sans-serif', color: '#FF3B6B' }}>{s.time_label}</p>
        </div>
      </div>

      {/* Booking summary */}
      <Section title="Booking Summary" testid="runsheet-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E8E5DF] border border-[#E8E5DF] rounded-lg overflow-hidden">
          {[
            ['Package', s.package_name || '—'],
            ['Duration', s.package_duration || '—'],
            ['Date', s.date || '—'],
            ['Time', s.time_label || '—'],
            ['Birthday child', s.child_name || '—'],
            ['Age', (s.child_age != null && s.child_age !== '') ? s.child_age : '—'],
            ['Guests', s.extra_guest_count ? `${s.guest_count} (${s.included_guests ?? '?'} inc + ${s.extra_guest_count} extra)` : (s.guest_count ?? '—')],
            ['Party host', s.host_name || 'Unassigned'],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#FFFDF8] p-2.5">
              <p className="text-[9px] uppercase tracking-wider text-[#6B6B7A]">{k}</p>
              <p className="font-semibold text-[#1E1E24] break-words">{v}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Contact blocks */}
      <section className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="text-[9px] uppercase tracking-wider text-[#6B6B7A] mb-1">Customer</p>
          <p className="font-semibold">{s.customer_name}</p>
          <p className="text-xs text-[#6B6B7A]">{s.customer_phone || '—'}</p>
          <p className="text-xs text-[#6B6B7A] break-all">{s.customer_email}</p>
          {s.customer_address && <p className="text-xs text-[#6B6B7A]">{s.customer_address}</p>}
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="text-[9px] uppercase tracking-wider text-[#6B6B7A] mb-1">Location</p>
          <p className="font-semibold">{loc.name}</p>
          <p className="text-xs text-[#6B6B7A]">{loc.address_line1}</p>
          <p className="text-xs text-[#6B6B7A]">{loc.address_line2} · {loc.phone}</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="text-[9px] uppercase tracking-wider text-[#6B6B7A] mb-1">Party host (staff)</p>
          <p className="font-semibold">{s.host_name}</p>
          <p className="text-xs text-[#6B6B7A]">Arrive 20 min early for setup</p>
        </div>
      </section>

      {/* Birthday child allergy + waiver */}
      {(bc.allergies?.length > 0 || bc.allergy_other) && (
        <section className="mb-4 rounded-lg border-2 border-[#D1344D] bg-[#FFE4E1] p-4" data-testid="runsheet-print-allergies">
          <p className="text-sm font-semibold text-[#D1344D]">⚠ Birthday child — allergy alert</p>
          <p className="mt-1 text-sm">
            {allergyChips(bc.allergies).map((c, i) => <React.Fragment key={i}>{i > 0 && ', '}{c}</React.Fragment>)}
            {bc.allergy_other && <span>{bc.allergies?.length ? ', ' : ''}{bc.allergy_other}</span>}
          </p>
        </section>
      )}
      <p className="text-xs text-[#6B6B7A] mb-5" data-testid="runsheet-bc-waiver">
        Birthday child waiver:{' '}
        {bc.waiver_signed
          ? <span className="text-[#06A47A] font-semibold">Signed</span>
          : <span className="text-[#D1344D] font-semibold">Not on file</span>}
      </p>

      {/* Add-ons & extras grouped by category */}
      <Section title="Add-ons & Extras" testid="runsheet-addons">
        {(!rs.addon_groups || rs.addon_groups.length === 0) ? (
          <p className="text-xs text-[#6B6B7A] italic">No add-ons or extras on this booking.</p>
        ) : rs.addon_groups.map((grp) => (
          <div key={grp.category} className="mb-3">
            <p className="text-xs font-semibold text-[#1E1E24] mb-1">{grp.category}</p>
            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {grp.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-1.5">
                  <span>{it.name}</span>
                  <span className="font-semibold">x{it.qty}{it.unit_label ? ` ${it.unit_label}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Section>

      {/* Design studio */}
      {rs.designs?.length > 0 && (
        <Section title="Design Studio Selections" testid="runsheet-designs">
          <ul className="space-y-1">
            {rs.designs.map((d, i) => (
              <li key={i} className="border border-gray-200 rounded-lg px-3 py-1.5">
                <span className="font-semibold">{d.garment}</span> · {d.source}
                {d.label ? <> · Label: <span className="font-semibold">{d.label}</span></> : null}
                {' · '}Qty: {d.qty}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Offers / discounts (names only) */}
      {rs.offers?.length > 0 && (
        <Section title="Offers & Discounts (confirmation only)" testid="runsheet-offers">
          <ul className="list-disc pl-5 space-y-0.5">
            {rs.offers.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </Section>
      )}

      {/* Guest list */}
      {rs.guests?.length > 0 && (
        <Section
          title={`Guest List — ${gsum.attending} attending · ${gsum.waivers_signed} waivers signed · ${gsum.forms_pending} form(s) pending`}
          testid="runsheet-guests"
        >
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-[#FFFDF8] text-left">
                <th className="py-1.5 px-2 font-semibold w-6">#</th>
                <th className="py-1.5 px-2 font-semibold">Child</th>
                <th className="py-1.5 px-2 font-semibold">Allergies / medical</th>
                <th className="py-1.5 px-2 font-semibold">Waiver</th>
                <th className="py-1.5 px-2 font-semibold">Emergency contact</th>
              </tr>
            </thead>
            <tbody>
              {rs.guests.map((g, i) => {
                const low = new Set((g.allergies || []).map((a) => String(a).toLowerCase().replace(/ /g, '_')));
                const rowBg = [...low].some((a) => CRITICAL_ALLERGIES.has(a))
                  ? 'bg-[#FFE4E1]'
                  : [...low].some((a) => WARNING_ALLERGIES.has(a)) ? 'bg-[#FFF4CE]' : '';
                const hasAllergy = (g.allergies?.length || g.allergy_other);
                return (
                  <tr key={i} className={`border-t border-gray-100 ${rowBg}`} data-testid={`runsheet-guest-${i}`}>
                    <td className="py-1.5 px-2 align-top">{i + 1}</td>
                    <td className="py-1.5 px-2 align-top font-semibold">
                      {g.name}
                      {g.attendee_name && g.attendee_name !== g.name && (
                        <div className="text-[10px] text-[#6B6B7A] font-normal">({g.attendee_name})</div>
                      )}
                    </td>
                    <td className="py-1.5 px-2 align-top">
                      {hasAllergy ? (
                        <>
                          {allergyChips(g.allergies).map((c, j) => <React.Fragment key={j}>{j > 0 && ' · '}{c}</React.Fragment>)}
                          {g.allergy_other && <span>{g.allergies?.length ? ' · ' : ''}{g.allergy_other}</span>}
                          {g.medical_conditions?.length > 0 && (
                            <span className="text-[#6B6B7A]"> [{g.medical_conditions.map(titleCase).join(', ')}]</span>
                          )}
                        </>
                      ) : g.form_submitted
                        ? <span className="text-[#06A47A]">None reported</span>
                        : <span className="text-[#6B6B7A] italic">No form submitted</span>}
                    </td>
                    <td className="py-1.5 px-2 align-top">
                      {g.waiver_signed
                        ? <span className="text-[#06A47A] font-semibold">Signed</span>
                        : <span className="text-[#D1344D]">Pending</span>}
                    </td>
                    <td className="py-1.5 px-2 align-top text-[#6B6B7A]">
                      {g.form_submitted ? `${g.emergency_name || ''} ${g.emergency_phone ? '· ' + g.emergency_phone : ''}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-[#6B6B7A] mt-2">
            <span className="text-[#D1344D] font-bold">Red</span> = anaphylactic risk (peanuts / tree nuts / shellfish).{' '}
            <span className="text-[#B3770A] font-bold">Amber</span> = other allergies.
          </p>
        </Section>
      )}

      {/* Special instructions & notes */}
      {(rs.notes?.special_instructions || rs.notes?.edible_notes || rs.notes?.internal_notes) && (
        <Section title="Special Instructions & Notes" testid="runsheet-notes">
          <div className="space-y-1">
            {rs.notes.special_instructions && <p><span className="font-semibold">Booking notes:</span> {rs.notes.special_instructions}</p>}
            {rs.notes.edible_notes && <p><span className="font-semibold">Cake / edible notes:</span> {rs.notes.edible_notes}</p>}
            {rs.notes.internal_notes && <p><span className="font-semibold">Internal notes:</span> {rs.notes.internal_notes}</p>}
          </div>
        </Section>
      )}

      {/* Party run-of-show */}
      <Section title="Party Run-of-Show" testid="runsheet-flow">
        <ul>{(rs.party_flow || []).map((it, i) => <Check key={i}>{it}</Check>)}</ul>
      </Section>

      {/* Setup & prep */}
      <Section title="Setup & Prep Requirements" testid="runsheet-setup">
        <ul>{(rs.setup_requirements || []).map((it, i) => <Check key={i}>{it}</Check>)}</ul>
      </Section>

      {/* Host reminders */}
      <Section title="Host Reminders" testid="runsheet-reminders">
        <ul className="list-disc pl-5 space-y-0.5">
          {(rs.host_reminders || []).map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      </Section>

      {/* Emergency contacts */}
      <Section title="Emergency Contacts" testid="runsheet-emergency">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#FFFDF8] text-left">
              <th className="py-1.5 px-2 font-semibold">Who</th>
              <th className="py-1.5 px-2 font-semibold">Name</th>
              <th className="py-1.5 px-2 font-semibold">Phone</th>
            </tr>
          </thead>
          <tbody>
            {(rs.emergency_contacts || []).map((ec, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1.5 px-2">{ec.label}</td>
                <td className="py-1.5 px-2 font-semibold">{ec.name}</td>
                <td className="py-1.5 px-2 font-semibold">{ec.phone}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <p className="mt-6 pt-3 border-t border-gray-200 text-[10px] text-[#9a9aa6] text-center">
        Printed {printedAt} · Creative Birthdays · {loc.name} · {loc.phone} · Operational document — no pricing.
      </p>
    </div>
  );
};

export default AdminRunSheet;
